import { useState } from "react";
import {
  KeyIcon,
  CheckCircleIcon,
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import { trpc } from "../../lib/api/trpc";
import { formatDistanceToNow } from "date-fns";

type KeyStatus = {
  isConfigured: boolean;
  keyHint: string | null;
  updatedAt: string | null;
};

type AiKeyStatusResponse = {
  anthropic: KeyStatus;
  openai: KeyStatus;
};

function SetKeyModal({
  provider,
  onClose,
  onSuccess,
}: {
  provider: "anthropic" | "openai";
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const utils = trpc.useContext();

  const handleSubmit = async () => {
    if (!apiKey.trim()) return;
    setIsValidating(true);
    setError(null);

    try {
      // @ts-ignore - tRPC type inference
      await utils.client.organization.setAiKey.mutate({
        provider,
        apiKey: apiKey.trim(),
      });
      // @ts-ignore
      await utils.organization.getAiKeyStatus.invalidate();
      onSuccess();
    } catch (err: any) {
      setError(err?.message || "Failed to save key");
    } finally {
      setIsValidating(false);
    }
  };

  const providerLabel = provider === "anthropic" ? "Anthropic" : "OpenAI";
  const providerUrl =
    provider === "anthropic" ? "console.anthropic.com" : "platform.openai.com";

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-surface rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-text-base">
              {providerLabel} API Key
            </h3>
            <button
              onClick={onClose}
              className="text-text-faint hover:text-text-muted"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-muted mb-1">
                API Key
              </label>
              <div className="relative">
                <input
                  type={showKey ? "text" : "password"}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={
                    provider === "anthropic" ? "sk-ant-..." : "sk-..."
                  }
                  className="w-full px-3 py-2 pr-10 border border-subtle rounded-lg bg-surface text-text-base text-sm font-mono focus:ring-2 focus:ring-accent focus:border-accent"
                />
                <button
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-text-faint hover:text-text-muted"
                >
                  {showKey ? (
                    <EyeSlashIcon className="h-4 w-4" />
                  ) : (
                    <EyeIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
              <p className="mt-1 text-xs text-text-faint">
                Get your API key at{" "}
                <span className="font-medium">{providerUrl}</span>
              </p>
            </div>

            {error && <p className="text-sm text-err">{error}</p>}

            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-text-muted bg-surface border border-subtle rounded-lg hover:bg-surface-2 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!apiKey.trim() || isValidating}
                className="px-4 py-2 text-sm font-medium text-white bg-accent rounded-lg hover:bg-accent-strong disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isValidating ? "Validating..." : "Validate and save"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProviderCard({
  provider: _provider,
  label,
  description,
  status,
  onRemove,
  onOpenModal,
}: {
  provider: "anthropic" | "openai";
  label: string;
  description: string;
  status: KeyStatus;
  onRemove: () => void;
  onOpenModal: () => void;
}) {
  const [confirmRemove, setConfirmRemove] = useState(false);

  return (
    <div className="bg-surface rounded-lg shadow-box p-6">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold text-text-base">{label}</h3>
          <p className="text-sm text-text-faint mt-1">{description}</p>
        </div>
        <KeyIcon className="h-6 w-6 text-text-faint" />
      </div>

      <div className="mt-4">
        {status.isConfigured ? (
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-ok" />
            <span className="text-sm text-ok font-medium">
              Custom key active
            </span>
            <span className="text-sm text-text-faint font-mono">
              ...{status.keyHint}
            </span>
          </div>
        ) : (
          <p className="text-sm text-text-faint">Using platform default key</p>
        )}

        {status.isConfigured && status.updatedAt && (
          <p className="text-xs text-text-faint mt-1">
            Last updated{" "}
            {formatDistanceToNow(new Date(status.updatedAt), {
              addSuffix: true,
            })}
          </p>
        )}
      </div>

      <div className="mt-4 flex gap-3">
        <button
          onClick={onOpenModal}
          className="px-4 py-2 text-sm font-medium text-white bg-accent rounded-lg hover:bg-accent-strong transition-colors"
        >
          {status.isConfigured ? "Replace key" : "Add key"}
        </button>

        {status.isConfigured && !confirmRemove && (
          <button
            onClick={() => setConfirmRemove(true)}
            className="px-4 py-2 text-sm font-medium text-text-muted bg-surface border border-subtle rounded-lg hover:bg-surface-2 transition-colors"
          >
            Remove
          </button>
        )}

        {confirmRemove && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                onRemove();
                setConfirmRemove(false);
              }}
              className="px-3 py-2 text-sm font-medium text-err border border-err rounded-lg hover:bg-err-bg transition-colors"
            >
              Confirm remove
            </button>
            <button
              onClick={() => setConfirmRemove(false)}
              className="px-3 py-2 text-sm font-medium text-text-faint hover:text-text-muted"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function AiKeysPage() {
  const [modalProvider, setModalProvider] = useState<
    "anthropic" | "openai" | null
  >(null);
  const utils = trpc.useContext();

  // @ts-ignore - tRPC type inference
  const { data, isLoading } = trpc.organization.getAiKeyStatus.useQuery();
  const status = data as AiKeyStatusResponse | undefined;

  const handleRemove = async (provider: "anthropic" | "openai") => {
    try {
      // @ts-ignore
      await utils.client.organization.removeAiKey.mutate({ provider });
      // @ts-ignore
      await utils.organization.getAiKeyStatus.invalidate();
    } catch {
      // error handled silently
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        {[1, 2].map((i) => (
          <div key={i} className="bg-surface rounded-lg shadow-box p-6">
            <div className="h-5 w-32 bg-surface-2 rounded mb-3" />
            <div className="h-4 w-48 bg-surface-2 rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-text-base">AI Keys</h2>
        <p className="mt-1 text-sm text-text-muted">
          Configure your organisation&apos;s API keys for document processing
          and AI responses. If not configured, the platform&apos;s default keys
          are used.
        </p>
      </div>

      <div className="space-y-6">
        <ProviderCard
          provider="anthropic"
          label="Anthropic"
          description="Used for document extraction, fact extraction, and chat responses"
          status={
            status?.anthropic || {
              isConfigured: false,
              keyHint: null,
              updatedAt: null,
            }
          }
          onRemove={() => handleRemove("anthropic")}
          onOpenModal={() => setModalProvider("anthropic")}
        />

        <ProviderCard
          provider="openai"
          label="OpenAI"
          description="Used for generating search embeddings (text-embedding-3-large)"
          status={
            status?.openai || {
              isConfigured: false,
              keyHint: null,
              updatedAt: null,
            }
          }
          onRemove={() => handleRemove("openai")}
          onOpenModal={() => setModalProvider("openai")}
        />

        <div className="bg-surface-2 border border-subtle rounded-lg p-4">
          <p className="text-sm text-text-muted">
            Your API keys are encrypted at rest and never returned after saving.
            The platform&apos;s default keys are used as a fallback if you have
            not configured your own. Keys are used only for your
            organisation&apos;s document processing and chat queries.
          </p>
        </div>
      </div>

      {modalProvider && (
        <SetKeyModal
          provider={modalProvider}
          onClose={() => setModalProvider(null)}
          onSuccess={() => setModalProvider(null)}
        />
      )}
    </div>
  );
}
